'use client';
import NavbarBrand from '@components/Navbar/_Brand';
import NavbarShoppingCart from '@components/Navbar/_ShoppingCart';
import UserAvatar from '@components/Navbar/_UserAvatar';
import MobileNav, { MobileNavProps } from '@components/Navigation/_MobileNav';
import NavDropdownWrapper from '@components/Navigation/_NavDropdownWrapper';
import Search from '@components/Navigation/_GlobalSearch';
import SitewideBanner, { type SitewideBannerProps } from '@components/Navigation/_SitewideBanner';
import { useKeyboardNavigation } from '@hooks/useKeyboardNavigation';
import { useOnRouteChange } from '@hooks/useOnRouteChange';
import Button from '@repo/ui/src/components/Button';
import { Anchor } from '@repo/ui/src/components/Anchor';
import CloseIcon from '@repo/ui/src/components/Icons/CloseIcon';
import HamburgerIcon from '@repo/ui/src/components/Icons/HamburgerIcon';
import HeaderPaddingWrapper from '@repo/ui/src/components/HeaderPaddingWrapper';
import { useScrollLock } from '@repo/ui/src/hooks/useScrollLock';
import { classNames } from '@repo/ui/src/utils/classNames';
import FocusTrap from 'focus-trap-react';
import debounce from 'lodash/debounce';
import noop from 'lodash/noop';
import React, { useState } from 'react';
import { LinkProps } from '@repo/ui/src/types/link';
import { MetaProps } from '@repo/ui/src/types';
import SecondaryNav, { SecondaryNavProps } from '@components/Navigation/_SecondaryNav';
import NavDropdown, { NavDropdownProps } from '@components/Navigation/_NavDropdown';
import DynamicComponent from '@repo/ui/src/components/DynamicComponent';
import SearchIcon from '@repo/ui/src/components/Icons/SearchIcon';
import { navigationClicked } from '@repo/ui/src/services/analytics';

export interface NavItemProps {
  _meta: MetaProps;
  title: string;
  titleAsLink?: LinkProps;
  content: NavDropdownProps | SecondaryNavProps;
}

export interface NavigationProps {
  searchLink: LinkProps;
  navItems: Array<NavItemProps>;
  sitewideBanner: SitewideBannerProps;
  account?: MobileNavProps['account'];
}

const componentTypes = {
  'https://traeger.com/v2/secondaryNav.json': SecondaryNav,
  'https://traeger.com/v2/navDropdown.json': NavDropdown,
};

export const isDropdown = (item: NavItemProps) =>
  item.content?._meta?.schema === 'https://traeger.com/v2/navDropdown.json';

const NavigationAnimationContext = React.createContext({
  setHeight: noop,
  setOffset: noop,
});

export const useNavigationContext = () => React.useContext(NavigationAnimationContext);

const Navigation = ({ navItems, searchLink, sitewideBanner, account }: NavigationProps) => {
  const { lock, unlock } = useScrollLock();
  const sitewideBannerRef = React.useRef<HTMLDivElement>(null);
  const headerRef = React.useRef<HTMLElement>(null);
  const ref = React.useRef<HTMLDivElement>(null);
  const [openDropdown, setOpenDropdown] = useState<number | null>(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [offset, setOffset] = useState(0);
  const [dropdownHeight, setDropdownHeight] = useState(0);
  const [dropdownOffset, setDropdownOffset] = useState(0);

  React.useEffect(() => {
    const getOffsetHeight = () =>
      (headerRef.current?.getBoundingClientRect().height ?? 0) +
      (sitewideBannerRef.current?.getBoundingClientRect().height ?? 0);
    setOffset(getOffsetHeight());

    const handleResize = debounce(() => {
      setOffset(getOffsetHeight());
    });

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  React.useEffect(() => {
    if (openDropdown != null) {
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape' && openDropdown != null) {
          (
            headerRef.current?.querySelector(
              `[data-testid="main-nav-${openDropdown}"]`,
            ) as HTMLElement
          )?.focus();
          setOpenDropdown(null);
        }
      };

      window.addEventListener('keydown', handleEscape);
      return () => window.removeEventListener('keydown', handleEscape);
    }
  }, [openDropdown]);

  useOnRouteChange(() => {
    setMobileDrawerOpen(false);
    setOpenDropdown(null);
  });

  React.useEffect(() => {
    if (mobileDrawerOpen || openDropdown != null) {
      lock();
    } else {
      unlock();
    }
  }, [mobileDrawerOpen, openDropdown, lock, unlock]);

  const handleKeyDown = useKeyboardNavigation({
    items: navItems,
    onChange: (index) => {
      setOpenDropdown(index);
      (
        headerRef.current?.querySelector(`[data-testid="main-nav-${index}"]`) as HTMLElement
      )?.focus();
    },
  });

  if (!navItems) return null;

  return (
    <>
      <Button
        as={Anchor}
        link="#main-content"
        size="small"
        className={classNames(
          'tw-z-[1100] tw-absolute -tw-top-20 tw-transition-top tw-ease-in-out tw-duration-[250] focus:tw-top-1 tw-left-1',
        )}
      >
        {'Skip to Main Content'}
      </Button>
      <div ref={sitewideBannerRef}>{sitewideBanner && <SitewideBanner {...sitewideBanner} />}</div>
      <FocusTrap active={mobileDrawerOpen}>
        <header
          className={classNames(
            'tw-sticky tw-top-0 tw-z-[1001] tw-block',
            openDropdown != null &&
            'after:tw-opacity-50 after:tw-bg-black after:tw-fixed after:tw-inset-0 after:tw-transition-opacity after:-tw-z-30',
          )}
          ref={headerRef}
          data-testid="overlay-header"
        >
          <NavigationAnimationContext.Provider
            value={{ setHeight: setDropdownHeight, setOffset: setDropdownOffset }}
          >
            <div
              ref={ref}
              onMouseLeave={() => setOpenDropdown(null)}
              className={classNames(
                'tw-bg-neutral-700 !tw-text-white tw-flex tw-flex-col',
                'after:tw-inset-x-0 after:tw-top-full after:-tw-z-20 after:tw-absolute after:tw-block after:tw-bg-warm-100 after:tw-h-[calc(var(--dropdown-height,0)+var(--dropdown-offset,0))] after:tw-max-h-[calc(85vh-var(--tra-nav-height))] after:[transition:height_500ms,opacity_300ms] after:tw-duration-500',
                openDropdown === null
                  ? 'after:tw-opacity-0 after:[transition:height_500ms_500ms,opacity_300ms] '
                  : 'after:tw-opacity-100',
              )}
              aria-label="main"
              style={{
                // @ts-expect-error unknown style
                '--dropdown-height': `${dropdownHeight}px`,
                '--dropdown-offset': `${dropdownOffset}px`,
              }}
            >
              <HeaderPaddingWrapper
                className="tw-min-h-navHeight tw-w-full tw-max-h-[500px] tw-items-center tw-grid tw-grid-flow-col tw-justify-between md:tw-grid-cols-[1fr_auto_1fr] tw-gap-5 tw-self-start"
                data-testid="desktop-nav"
                as="nav"
                aria-label="desktop"
              >
                <NavbarBrand />
                <ul className="tw-hidden tw-list-none sm:tw-flex tw-justify-center tw-items-center tw-gap-x-10 tw-pl-0">
                  {navItems
                    .filter((item) => !!item.content)
                    .map((item, i) => {
                      const isOpen = openDropdown === i;
                      return (
                        <li key={i}>
                          <button
                            data-testid={`main-nav-${i}`}
                            data-index={i}
                            onKeyDown={handleKeyDown}
                            onClick={() => {
                              setOpenDropdown((curr) => (curr === i ? null : i));
                              navigationClicked({
                                nav_type: 'Top Navigation',
                                clicked_text: navItems[i].title ?? '',
                                primary_header: navItems[i].title ?? '',
                              });
                            }}
                            className={classNames(
                              'tra-text-web-sub-2 tw-relative after:tw-transition-all after:tw-duration-300 after:tw-opacity-100 after:tw-absolute after:tw-inset-x-0 after:-tw-bottom-2 after:tw-w-full after:tw-h-[4px] after:tw-bg-brand-300 after:tw-scale-0 after:tw-origin-center hover:after:tw-scale-100',
                              isOpen && 'after:tw-scale-100',
                            )}
                            aria-expanded={isOpen}
                            aria-controls={`nav-dropdown-${i}`}
                            aria-haspopup="true"
                          >
                            {item.title}
                          </button>
                          <NavDropdownWrapper
                            id={`nav-dropdown-${i}`}
                            open={isOpen}
                            isDropdown={isDropdown(item)}
                            onTransitionEnd={() => {
                              if (openDropdown === null) {
                                setDropdownHeight(0);
                                setDropdownOffset(0);
                              }
                            }}
                          >
                            <DynamicComponent
                              {...item.content}
                              _componentTypes={componentTypes}
                              searchLink={searchLink}
                              open={openDropdown === i && !!item.content}
                              primaryHeader={item?.title}
                            />
                          </NavDropdownWrapper>
                        </li>
                      );
                    })}
                </ul>
                <ul className="tw-list-none tw-pl-0 tw-flex tw-flex-row tw-items-center tw-justify-end md:tw-gap-4 -tw-mr-3 sm:-tw-mr-1">
                  <Anchor
                    className="tw-p-3 lg:tw-hidden"
                    aria-label="Search"
                    link={searchLink.link}
                  >
                    <SearchIcon className="tw-w-6" />
                  </Anchor>

                  <li>
                    <Search placeholder={searchLink.displayName} link={searchLink.link} />
                  </li>
                  <li>
                    <UserAvatar
                      className="tw-hidden md:tw-inline-block"
                      loggedInLabel={account?.loggedInLabel}
                      loggedOutText={account?.loggedOutText}
                    />
                  </li>
                  <li>
                    <NavbarShoppingCart />
                  </li>
                  <button
                    className="sm:tw-hidden tw-p-3"
                    aria-label={mobileDrawerOpen ? 'close navigation' : 'open navigation'}
                    onClick={() => {
                      setMobileDrawerOpen((currentlyOpen) => !currentlyOpen);
                    }}
                  >
                    {mobileDrawerOpen ? (
                      <CloseIcon className="tw-w-6" />
                    ) : (
                      <HamburgerIcon className="tw-w-6" />
                    )}
                  </button>
                </ul>
              </HeaderPaddingWrapper>
              <MobileNav
                open={mobileDrawerOpen}
                offset={offset}
                navItems={navItems}
                searchLink={searchLink}
                account={account}
                dealerLocator={sitewideBanner?.dealerLocatorLink}
              />
            </div>
          </NavigationAnimationContext.Provider>
        </header>
      </FocusTrap>
    </>
  );
};

export default Navigation;
